import React, { useEffect, useMemo, useRef, useState } from 'react';
import defImage from '../images/defImage.png';

function PopupCart({
  user,
  formData,
  addressData,
  cities,
  allStreets,
  cart,
  totalPrice,
  deliveryPrice,
  pizzaBoxes,
  setFormData,
  setAddressData,
  setCartData,
  handleCityChange,
  handleStreetSearch,
  handleOrderSubmit,
  openLoginPopup,
  closePopups,
  increaseItemAmount,
  decreaseItemAmount,
  removeItem
}) {
  const [streetQuery, setStreetQuery] = useState(addressData.street?.name || '');
  const [isStreetDropdownOpen, setIsStreetDropdownOpen] = useState(false);
  const streetPickerRef = useRef(null);
  const streetInputRef = useRef(null);

  const visibleStreets = useMemo(() => {
    const normalizedQuery = streetQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return allStreets;
    }

    return allStreets.filter((street) =>
      street.name.toLowerCase().includes(normalizedQuery)
    );
  }, [allStreets, streetQuery]);

  useEffect(() => {
    if (!addressData.city) {
      setStreetQuery('');
      setIsStreetDropdownOpen(false);
      return;
    }

    if (addressData.street?.name) {
      setStreetQuery(addressData.street.name);
      return;
    }

    setStreetQuery('');
  }, [addressData.city, addressData.street]);

  useEffect(() => {
    if (!isStreetDropdownOpen || !addressData.city) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      handleStreetSearch(addressData.city, streetQuery.trim());
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [addressData.city, handleStreetSearch, isStreetDropdownOpen, streetQuery]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (streetPickerRef.current && !streetPickerRef.current.contains(event.target)) {
        setIsStreetDropdownOpen(false);

        if (!addressData.street?.name) {
          setStreetQuery('');
        }
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [addressData.street]);

  const openStreetDropdown = async () => {
    if (!addressData.city) {
      alert('Сначала выберите город.');
      return;
    }

    setIsStreetDropdownOpen(true);

    if (!allStreets.length) {
      await handleStreetSearch(addressData.city, streetQuery.trim());
    }
  };

  const selectStreet = (street) => {
    setAddressData((prevState) => ({
      ...prevState,
      street: { id: street.id, name: street.name },
    }));
    setStreetQuery(street.name);
    setIsStreetDropdownOpen(false);
    streetInputRef.current?.blur();
  };

  const handleStreetInputChange = (event) => {
    const nextValue = event.target.value;

    setStreetQuery(nextValue);
    setIsStreetDropdownOpen(true);
    setAddressData((prevState) => ({
      ...prevState,
      street:
        prevState.street?.name === nextValue
          ? prevState.street
          : '',
    }));
  };

  return (
    <div className="popup-cart" onClick={(event) => event.stopPropagation()}>
      <button
        className="popup-cart__close-button"
        type="button"
        onClick={closePopups}
        aria-label="Закрыть корзину и вернуться к заказу"
      ></button>
      <h2 className="popup-cart__heading">Оформление заказа</h2>
      <h3 className="popup-cart__heading">Куда привезти?</h3>
      <div className="popup-cart__adress">
        <label className="popup-cart__adress-item popup-cart__adress-item1">
          В какой город?
          <select
            className="popup-cart__city-select"
            name="city"
            value={addressData.city}
            onChange={(event) => handleCityChange(event.target.value)}
          >
            <option value="">Выберите город</option>
            {cities.length > 0 ? (
              cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))
            ) : (
              <option>Загрузка...</option>
            )}
          </select>
          <span className="popup-cart__city-toggle-icon" aria-hidden="true"></span>
        </label>
        <label className="popup-cart__adress-item popup-cart__adress-item2">
          Улица
          <div
            className={`popup-cart__street-picker ${isStreetDropdownOpen ? 'popup-cart__street-picker_open' : ''}`}
            ref={streetPickerRef}
          >
            <input
              ref={streetInputRef}
              className="popup-cart__street-input"
              type="text"
              name="street"
              value={streetQuery}
              disabled={!addressData.city}
              placeholder={addressData.city ? 'Начните вводить улицу' : 'Сначала выберите город'}
              autoComplete="street-address"
              onFocus={openStreetDropdown}
              onClick={openStreetDropdown}
              onChange={handleStreetInputChange}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  setIsStreetDropdownOpen(false);
                }

                if (event.key === 'Enter' && visibleStreets.length > 0) {
                  event.preventDefault();
                  selectStreet(visibleStreets[0]);
                }
              }}
            />
            {streetQuery && (
              <button
                className="popup-cart__street-clear"
                type="button"
                aria-label="Очистить поиск улицы"
                onClick={() => {
                  setStreetQuery('');
                  setIsStreetDropdownOpen(true);
                  setAddressData((prevState) => ({
                    ...prevState,
                    street: '',
                  }));
                  handleStreetSearch(addressData.city, '');
                }}
              ></button>
            )}
            <button
              className="popup-cart__street-toggle"
              type="button"
              aria-label={isStreetDropdownOpen ? 'Скрыть список улиц' : 'Показать список улиц'}
              onClick={() => {
                if (isStreetDropdownOpen) {
                  setIsStreetDropdownOpen(false);
                  return;
                }

                openStreetDropdown();
              }}
            >
              <span className={`popup-cart__street-toggle-icon ${isStreetDropdownOpen ? 'popup-cart__street-toggle-icon_open' : ''}`}></span>
            </button>

            {isStreetDropdownOpen && (
              <div className="popup-cart__street-dropdown" role="listbox" aria-label="Список улиц">
                {visibleStreets.length > 0 ? (
                  visibleStreets.slice(0, 50).map((street) => (
                    <button
                      key={street.id}
                      className={`popup-cart__street-option ${addressData.street?.id === street.id ? 'popup-cart__street-option_active' : ''}`}
                      type="button"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        selectStreet(street);
                      }}
                    >
                      {street.name}
                    </button>
                  ))
                ) : (
                  <p className="popup-cart__street-empty">
                    По вашему запросу ничего не найдено.
                  </p>
                )}
              </div>
            )}
          </div>
        </label>
        <label className="popup-cart__adress-item popup-cart__adress-item3">
          Дом
          <input
            type="text"
            name="house"
            value={addressData.house}
            onChange={(event) => setAddressData({ ...addressData, house: event.target.value })}
            placeholder="Номер дома"
          />
        </label>
        <label className="popup-cart__adress-item popup-cart__adress-item4">
          Корпус
          <input
            type="text"
            name="building"
            value={addressData.building}
            onChange={(event) => setAddressData({ ...addressData, building: event.target.value })}
            placeholder="Корпус (если есть)"
          />
        </label>
        <label className="popup-cart__adress-item popup-cart__adress-item5">
          Подъезд
          <input
            type="text"
            name="entrance"
            value={addressData.entrance}
            onChange={(event) => setAddressData({ ...addressData, entrance: event.target.value })}
            placeholder="Подъезд"
          />
        </label>
        <label className="popup-cart__adress-item popup-cart__adress-item6">
          Этаж
          <input
            type="text"
            name="floor"
            value={addressData.floor}
            onChange={(event) => setAddressData({ ...addressData, floor: event.target.value })}
            placeholder="Этаж"
          />
        </label>
        <label className="popup-cart__adress-item popup-cart__adress-item7">
          Квартира
          <input
            type="text"
            name="flat"
            value={addressData.flat}
            onChange={(event) => setAddressData({ ...addressData, flat: event.target.value })}
            placeholder="Квартира"
          />
        </label>
        <label className="popup-cart__adress-item popup-cart__adress-item8">
          Комментарий для курьера
          <textarea
            name="comment"
            value={addressData.comment}
            onChange={(event) => setAddressData({ ...addressData, comment: event.target.value })}
            placeholder="Здесь можно оставить комментарий для курьера"
          />
        </label>
      </div>
      <div className="popup-cart__total">
        <div className="popup-cart__spoons">
          <h3 className="popup-cart__heading popup-cart__heading_little">Сколько приборов положить?</h3>
          <div>
            <button
              className="popup-cart__button-plus popup-cart__button-plus-minus"
              onClick={() => {
                const guests = formData.guests || 1;
                if (guests > 1) {
                  setFormData({ ...formData, guests: guests - 1 });
                }
              }}
            >
              -
            </button>
            <span>{formData.guests || 1}</span>
            <button
              className="popup-cart__button-plus popup-cart__button-plus-plus"
              onClick={() => {
                const guests = formData.guests || 1;
                setFormData({ ...formData, guests: guests + 1 });
              }}
            >
              +
            </button>
          </div>
        </div>
        <div className="popup-cart__delivery">
          <h3>Стоимость доставки: {deliveryPrice} ₽</h3>
        </div>
        <h3 className="popup-cart__heading">Общая стоимость: {totalPrice + deliveryPrice} ₽</h3>
      </div>
      <button
        className="popup-cart__button-order"
        onClick={(event) => {
          if (user) {
            handleOrderSubmit(event);
          } else {
            openLoginPopup();
          }
        }}
      >
        Оформить заказ
      </button>
      <button className="popup-cart__button-add" type="button" onClick={closePopups}>
        Нажмите здесь, если нужно дополнить заказ
      </button>
      {cart.length === 0 ? (
        <p>Корзина пуста</p>
      ) : (
        <ul className="popup-cart__products">
          {cart.map((product) => (
            <li className="popup-cart__product" key={product.key}>
              <img className="popup-cart__product1" src={product.image || defImage} alt={product.name} />
              <p className="popup-cart__product2">{product.name}</p>
              <div className="popup-cart__product3" style={{ display: 'flex' }}>
                <button
                  className="popup-cart__button-plus popup-cart__button-plus-minus"
                  onClick={() => decreaseItemAmount(product.key)}
                ></button>
                <span>{product.amount}</span>
                <button
                  className="popup-cart__button-plus popup-cart__button-plus-plus"
                  onClick={() => increaseItemAmount(product.key)}
                ></button>
                <button
                  className="popup-cart__button-remove"
                  onClick={() => {
                    removeItem(product.key);
                  }}
                ></button>
              </div>
              <p className="popup-cart__product4">
                {(product.price + (product.modifiers?.reduce((sum, modifier) => sum + modifier.price, 0) || 0)) * product.amount} ₽
              </p>
              <label className="popup-cart__adress-item popup-cart__product5">
                <textarea
                  name="comment"
                  value={product.comment || ''}
                  onChange={(event) => {
                    const newCart = [...cart];
                    const index = cart.findIndex((item) => item.key === product.key);
                    newCart[index].comment = event.target.value;
                    setCartData(newCart);
                  }}
                  placeholder="Здесь можно оставить комментарий для повара"
                />
              </label>
              {product.modifiers && product.modifiers.length > 0 && (
                <ul className="popup-cart__product6">
                  {product.modifiers.map((modifier, modIndex) => (
                    <li key={modIndex}>
                      {modifier.name} - {modifier.price} ₽
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          {pizzaBoxes.pizzaBox30 && (
            <li className="popup-cart__pizza-box" key={pizzaBoxes.pizzaBox30.id}>
              В заказ добавлена {pizzaBoxes.pizzaBox30.name}: {pizzaBoxes.pizzaBox30.amount} шт. - {pizzaBoxes.pizzaBox30.price * pizzaBoxes.pizzaBox30.amount}₽
            </li>
          )}
          {pizzaBoxes.pizzaBox40 && (
            <li className="popup-cart__pizza-box" key={pizzaBoxes.pizzaBox40.id}>
              В заказ будет добавлена {pizzaBoxes.pizzaBox40.name}: {pizzaBoxes.pizzaBox40.amount} шт. - {pizzaBoxes.pizzaBox40.price * pizzaBoxes.pizzaBox40.amount}₽
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default PopupCart;
